import { portalText } from "../../i18n/portal.js";
/**
 * "Talk in the browser" — what the tester sees, as data and words (plan-004 WC-51, FR-BV-9,
 * design §17.12's "What each person sees, by failure", `design/WeTalk.dc.html` 3728–3736).
 *
 * One phase at a time, from the click to the end:
 *
 *   idle ─ Start ─▶ requesting_microphone ─ refused ─▶ microphone_refused   (no api request, AC-BV-7)
 *                          │ granted
 *                          ▼
 *                        arming (the api) ─ refused ─▶ refused              ("Nothing was charged.")
 *                          │ 201
 *                          ▼
 *                     connecting ─▶ live ⇄ queued / reconnecting ─▶ ended   (duration · "Tests are free.")
 *                          └──────────────────────────────────────▶ failed  (never joined; nothing billed)
 *
 * The words are the tester's, never a code: design §17.12 fixes the microphone and relay sentences, the
 * refusal sentences are the api's own (each ends "Nothing was charged."), and every failure says what to
 * do next. Nothing here touches React, the gateway or the module, so every sentence is unit-tested.
 */
import type {
  BrowserVoiceEndReason,
  BrowserVoiceFailureReason,
  MicrophoneResult,
} from "@wetalk/webrtc-client";

import { formatDuration } from "../../money.js";

/** What the microphone prompt answered when it did not grant one. */
export type MicrophoneRefusal = Exclude<MicrophoneResult, { readonly kind: "granted" }>;

export type BrowserVoicePhase =
  | { readonly kind: "idle" }
  /** The browser's microphone prompt is open. Nothing has been asked of the api. */
  | { readonly kind: "requesting_microphone" }
  /** The microphone is granted and the api is arming the test. */
  | { readonly kind: "arming" }
  | { readonly kind: "connecting" }
  /** `sinceMs`: epoch milliseconds the agent's audio began (a reconnect does not restart it). */
  | { readonly kind: "live"; readonly sinceMs: number }
  /** Every line busy: on hold, which is free (the runtime's `queued` notice). */
  | { readonly kind: "queued" }
  | { readonly kind: "reconnecting" }
  /** `endedHere`: this page's Hang up ended it (not another tab, the agent or the network). */
  | { readonly kind: "ended"; readonly reason: BrowserVoiceEndReason; readonly endedHere: boolean }
  /** It never joined, so nothing was billed. */
  | { readonly kind: "failed"; readonly reason: BrowserVoiceFailureReason }
  | { readonly kind: "microphone_refused"; readonly refusal: MicrophoneRefusal }
  /**
   * The api refused the start, in its words. `neutral`: "not possible right now" (a 503 — the model
   * resting, the relay away, every line busy), never a red failure. `retry`: offer Try again (AC-BV-6).
   */
  | { readonly kind: "refused"; readonly message: string; readonly neutral: boolean; readonly retry: boolean };

export type BrowserVoicePhaseKind = BrowserVoicePhase["kind"];

/** While a test is under way: Start is not offered, Hang up is (once there is something to hang up). */
export function isUnderWay(phase: BrowserVoicePhase): boolean {
  switch (phase.kind) {
    case "requesting_microphone":
    case "arming":
    case "connecting":
    case "live":
    case "queued":
    case "reconnecting":
      return true;
    default:
      return false;
  }
}

/** Joined or joining the relay: the module holds the microphone and Hang up leaves. */
export function isConnected(phase: BrowserVoicePhase): boolean {
  return phase.kind === "connecting" || phase.kind === "live" || phase.kind === "queued" || phase.kind === "reconnecting";
}

/** The microphone did not come: a heading and how to fix it (design §17.12, AC-BV-7). */
export function microphoneWordsOf(refusal: MicrophoneRefusal): { readonly title: string; readonly fix: string } {
  switch (refusal.kind) {
    case "blocked":
      return {
        title: portalText("operations", "microphone_blocked_7b63697f"),
        fix: portalText("operations", "your_browser_blocked_the_microphone_allow_it_for_this_site_f7aef6cb"),
      };
    case "no_microphone":
      return { title: portalText("operations", "no_microphone_found_b47abe77"), fix: portalText("operations", "no_microphone_was_found_plug_one_in_or_use_a_phone_test_51439b89") };
    case "unavailable":
      switch (refusal.detail) {
        case "in_use":
          return {
            title: portalText("operations", "microphone_in_use_0134aed5"),
            fix: portalText("operations", "another_app_is_using_the_microphone_close_it_then_press_st_5553e850"),
          };
        case "insecure_context":
          return {
            title: portalText("operations", "microphone_unavailable_4e254cad"),
            fix: portalText("operations", "browsers_share_a_microphone_only_with_a_secure_https_page_abf2f635"),
          };
        case "unsupported":
          return {
            title: portalText("operations", "microphone_unavailable_4e254cad"),
            fix: portalText("operations", "this_browser_cannot_share_a_microphone_with_a_web_page_use_bada4fc9"),
          };
      }
  }
}

/** Why the conversation ended, in one sentence. */
export function endedWordsOf(reason: BrowserVoiceEndReason, endedHere: boolean, maxDurationSecond: number): string {
  switch (reason) {
    case "hung_up":
      return endedHere
        ? portalText("operations", "you_hung_up_8554b6ab")
        : portalText("operations", "the_test_was_ended_elsewhere_starting_a_browser_test_on_th_d463a5c4");
    case "ended_by_voice_agent":
      return portalText("operations", "the_agent_ended_the_conversation_a7761acd");
    case "max_duration_reached":
      return portalText("operations", "the_test_reached_its_value_minute_limit_fa41ecc5", { v0: String(String(Math.round(maxDurationSecond / 60))) });
    case "connection_lost":
      return portalText("operations", "connection_lost_the_test_ended_592fac95");
    case "unavailable":
      return portalText("operations", "the_agent_could_not_take_the_conversation_so_the_test_ende_a50e76f6");
  }
}

/** Why it never joined, and what to do (nothing was billed: it never started). */
export function failedWordsOf(reason: BrowserVoiceFailureReason): string {
  switch (reason) {
    case "join_deadline_passed":
      return portalText("operations", "the_test_took_too_long_to_connect_and_was_cancelled_nothin_ef7f3bf6");
    case "unreachable":
      return portalText("operations", "this_browser_could_not_reach_wetalk_s_browser_voice_relay_a76e9158");
    case "refused":
      return portalText("operations", "wetalk_s_browser_voice_relay_refused_this_test_nothing_was_330edff1");
  }
}

/** The status beside the controls while a test is under way. */
export function underWayWordsOf(phase: BrowserVoicePhase, nowMs: number): string | null {
  switch (phase.kind) {
    case "requesting_microphone":
      return portalText("operations", "allow_the_microphone_in_your_browser_s_prompt_6c22c54e");
    case "arming":
    case "connecting":
      return portalText("operations", "connecting_to_the_agent_e6d9ad69");
    case "live":
      return formatDuration(elapsedSecondOf(phase.sinceMs, nowMs));
    case "queued":
      return portalText("operations", "on_hold_all_of_the_agent_s_lines_are_busy_it_answers_when_5ae85d3f");
    case "reconnecting":
      return portalText("operations", "reconnecting_the_connection_dropped_for_a_moment_6c723ebf");
    default:
      return null;
  }
}

/** Whole seconds since `sinceMs`, never negative. */
export function elapsedSecondOf(sinceMs: number, nowMs: number): number {
  return Math.max(0, Math.floor((nowMs - sinceMs) / 1000));
}

/** The final-minute countdown (from the runtime's notice), or `null` when not counting. */
export function countdownWordsOf(untilMs: number | null, nowMs: number, maxDurationSecond: number): string | null {
  if (untilMs === null) return null;
  const left = Math.max(0, Math.ceil((untilMs - nowMs) / 1000));
  return portalText("operations", "the_test_ends_in_value_it_has_a_value_minute_limit_5202ecd2", { v0: String(formatDuration(left)), v1: String(String(Math.round(maxDurationSecond / 60))) });
}

/**
 * The prototype's `voiceMeta` under the controls: "idle · free" until it runs, then the language and "free" — tests
 * are free (plan-017 WAPP-B, owner answers Q4 and PQ-14), so it no longer says when billing starts.
 */
export function voiceMetaOf(phase: BrowserVoicePhase, languageName: string | null): string {
  if (isUnderWay(phase)) return portalText("operations", "test_console.browser_mic_language_free", { v0: String(languageName ?? portalText("operations", "the_agent_s_first_language_2fc3dc99")) });
  return portalText("operations", "test_console.browser_mic_idle_free");
}

/**
 * The level meter's bars (the prototype draws fourteen), each a scale from 0.18 to 1: the
 * microphone's level shaped a little differently per bar, so speech reads as movement.
 */
export const LEVEL_BAR_COUNT = 14;

export function levelBarsOf(level: number, active: boolean): readonly number[] {
  const clamped = Math.max(0, Math.min(1, level));
  return Array.from({ length: LEVEL_BAR_COUNT }, (_, index) => {
    if (!active) return 0.18;
    const shape = 0.55 + ((index * 5) % 9) / 18;
    return Math.round(Math.max(0.18, Math.min(1, clamped * shape * 1.6)) * 100) / 100;
  });
}
